(function(root, factory) {
  'use strict';

  var policy = factory();
  if (typeof module === 'object' && module.exports) module.exports = policy;
  if (root) root.EnigmaSingleCartLimit = policy;
})(typeof window !== 'undefined' ? window : null, function() {
  'use strict';

  var MAX_COPIES = 8;
  var LIMITED_PRODUCT_TYPE = 'magic single';

  function normalize(value) {
    return String(value == null ? '' : value).trim().toLowerCase().replace(/\s+/g, ' ');
  }

  function positiveInteger(value) {
    var number = Number(value);
    return Number.isInteger(number) && number > 0 ? number : null;
  }

  function nonNegativeInteger(value) {
    var number = Number(value);
    return Number.isInteger(number) && number >= 0 ? number : null;
  }

  function variantId(value) {
    var id = String(value == null ? '' : value).trim();
    return /^\d+$/.test(id) && Number(id) > 0 ? id : null;
  }

  function isLimitedProductType(value) {
    return normalize(value) === LIMITED_PRODUCT_TYPE;
  }

  function itemVariantId(item) {
    return variantId(item && (item.variant_id != null ? item.variant_id : item.id));
  }

  function itemIsLimited(item) {
    return Boolean(item && (
      item.enigma_single === true
      || item.enigmaSingle === true
      || isLimitedProductType(item.product_type || item.productType)
    ));
  }

  function cartItems(cart) {
    return cart && Array.isArray(cart.items) ? cart.items : [];
  }

  function quantityForVariant(cart, requestedVariantId, limitedOnly) {
    var id = variantId(requestedVariantId);
    if (!id) return 0;

    return cartItems(cart).reduce(function(total, item) {
      if (itemVariantId(item) !== id || (limitedOnly && !itemIsLimited(item))) return total;
      var quantity = nonNegativeInteger(item.quantity);
      return total + (quantity == null ? 0 : quantity);
    }, 0);
  }

  function aggregateLimitedQuantities(cart) {
    var quantities = {};
    cartItems(cart).forEach(function(item) {
      if (!itemIsLimited(item)) return;
      var id = itemVariantId(item);
      var quantity = nonNegativeInteger(item.quantity);
      if (!id || quantity == null) return;
      quantities[id] = (quantities[id] || 0) + quantity;
    });
    return quantities;
  }

  function readMetadataValue(metadata, camelName, snakeName) {
    if (!metadata) return '';
    if (metadata[camelName] != null) return metadata[camelName];
    if (snakeName && metadata[snakeName] != null) return metadata[snakeName];
    return '';
  }

  function effectiveLimit(metadata) {
    var management = normalize(readMetadataValue(metadata, 'inventoryManagement', 'inventory_management'));
    var policy = normalize(readMetadataValue(metadata, 'inventoryPolicy', 'inventory_policy'));
    var rawQuantity = readMetadataValue(metadata, 'inventoryQuantity', 'inventory_quantity');

    if (management !== 'shopify' || policy !== 'deny') {
      return { valid: false, limit: 0, reason: 'inventory_unknown' };
    }
    var inventoryQuantity = nonNegativeInteger(rawQuantity);
    if (inventoryQuantity == null) {
      return { valid: false, limit: 0, reason: 'inventory_unknown' };
    }
    return { valid: true, limit: Math.min(MAX_COPIES, inventoryQuantity), reason: '' };
  }

  function evaluateAddition(cart, request) {
    request = request || {};
    var limited = request.limited === true || isLimitedProductType(request.productType);
    if (!limited) {
      return { allowed: true, limited: false, reason: '', limit: Infinity, existing: 0, requested: 0, total: 0, remaining: Infinity };
    }

    var id = variantId(request.variantId);
    var quantity = positiveInteger(request.quantity);
    if (!id || quantity == null) {
      return { allowed: false, limited: true, reason: 'invalid_request', limit: 0, existing: 0, requested: quantity || 0, total: 0, remaining: 0 };
    }

    var cap = effectiveLimit(request.metadata);
    if (!cap.valid) {
      return { allowed: false, limited: true, reason: cap.reason, limit: cap.limit, existing: 0, requested: quantity, total: quantity, remaining: 0 };
    }

    var existing = quantityForVariant(cart, id, false);
    var total = existing + quantity;
    return {
      allowed: total <= cap.limit,
      limited: true,
      reason: total <= cap.limit ? '' : 'limit_exceeded',
      limit: cap.limit,
      existing: existing,
      requested: quantity,
      total: total,
      remaining: Math.max(0, cap.limit - existing)
    };
  }

  function findCartItem(cart, key) {
    var targetKey = String(key == null ? '' : key);
    return cartItems(cart).find(function(item) {
      return String(item && item.key != null ? item.key : '') === targetKey;
    }) || null;
  }

  function evaluateLineChange(cart, request) {
    request = request || {};
    var item = findCartItem(cart, request.key);
    var limited = request.limited === true || itemIsLimited(item);
    var quantity = nonNegativeInteger(request.quantity);
    if (!item || quantity == null) {
      return { allowed: false, limited: limited, reason: 'invalid_request', limit: 0, currentTotal: 0, nextTotal: 0, maximumForLine: 0 };
    }
    if (!limited) {
      return { allowed: true, limited: false, reason: '', limit: Infinity, currentTotal: 0, nextTotal: 0, maximumForLine: Infinity };
    }

    var id = itemVariantId(item) || variantId(request.variantId);
    var currentQuantity = nonNegativeInteger(item.quantity);
    if (!id || currentQuantity == null) {
      return { allowed: false, limited: true, reason: 'invalid_request', limit: 0, currentTotal: 0, nextTotal: 0, maximumForLine: 0 };
    }

    var cap = effectiveLimit(request.metadata);
    if (!cap.valid) {
      return { allowed: false, limited: true, reason: cap.reason, limit: cap.limit, currentTotal: 0, nextTotal: 0, maximumForLine: 0 };
    }

    var currentTotal = quantityForVariant(cart, id, false);
    var otherLines = Math.max(0, currentTotal - currentQuantity);
    var nextTotal = otherLines + quantity;
    var reducingViolation = nextTotal < currentTotal;
    var allowed = nextTotal <= cap.limit || reducingViolation;
    return {
      allowed: allowed,
      limited: true,
      reason: allowed ? '' : 'limit_exceeded',
      limit: cap.limit,
      currentTotal: currentTotal,
      nextTotal: nextTotal,
      maximumForLine: Math.max(0, cap.limit - otherLines)
    };
  }

  function lineMaximum(cart, request) {
    request = request || {};
    var item = findCartItem(cart, request.key);
    if (!item || !(request.limited === true || itemIsLimited(item))) return Infinity;

    var id = itemVariantId(item) || variantId(request.variantId);
    var currentQuantity = nonNegativeInteger(item.quantity);
    var cap = effectiveLimit(request.metadata);
    if (!id || currentQuantity == null || !cap.valid) return 0;

    var otherLines = Math.max(0, quantityForVariant(cart, id, false) - currentQuantity);
    return Math.max(0, cap.limit - otherLines);
  }

  function cartHasViolation(cart, additionalLimitedVariantIds) {
    var limitedIds = new Set((additionalLimitedVariantIds || []).map(String));
    var totals = {};
    cartItems(cart).forEach(function(item) {
      var id = itemVariantId(item);
      if (!id || (!itemIsLimited(item) && !limitedIds.has(id))) return;
      var quantity = nonNegativeInteger(item.quantity);
      if (quantity == null) return;
      totals[id] = (totals[id] || 0) + quantity;
    });
    return Object.keys(totals).some(function(id) { return totals[id] > MAX_COPIES; });
  }

  function datasetValue(dataset) {
    if (!dataset) return '';
    for (var index = 1; index < arguments.length; index += 1) {
      var name = arguments[index];
      if (name && dataset[name] != null && dataset[name] !== '') return dataset[name];
    }
    return '';
  }

  function metadataFromDataset(dataset) {
    return {
      inventoryManagement: datasetValue(dataset, 'inventoryManagement', 'inventory_management', 'enigmaInventoryManagement', 'enigma_inventory_management'),
      inventoryPolicy: datasetValue(dataset, 'inventoryPolicy', 'inventory_policy', 'enigmaInventoryPolicy', 'enigma_inventory_policy'),
      inventoryQuantity: datasetValue(dataset, 'inventoryQuantity', 'inventory_quantity', 'enigmaInventoryQuantity', 'enigma_inventory_quantity')
    };
  }

  function datasetDeclaresAny(dataset) {
    if (!dataset) return false;
    for (var index = 1; index < arguments.length; index += 1) {
      var name = arguments[index];
      if (name && dataset[name] != null) return true;
    }
    return false;
  }

  function metadataFieldsDeclared(dataset) {
    return Boolean(
      dataset
      && datasetDeclaresAny(dataset, 'inventoryManagement', 'inventory_management', 'enigmaInventoryManagement', 'enigma_inventory_management')
      && datasetDeclaresAny(dataset, 'inventoryPolicy', 'inventory_policy', 'enigmaInventoryPolicy', 'enigma_inventory_policy')
      && datasetDeclaresAny(dataset, 'inventoryQuantity', 'inventory_quantity', 'enigmaInventoryQuantity', 'enigma_inventory_quantity')
    );
  }

  function metadataIsComplete(metadata) {
    return Boolean(
      metadata
      && metadata.inventoryManagement !== ''
      && metadata.inventoryPolicy !== ''
      && metadata.inventoryQuantity !== ''
    );
  }

  function selectCompleteMetadata(primary, fallbacks) {
    if (metadataIsComplete(primary)) return primary;
    var candidates = Array.isArray(fallbacks) ? fallbacks : [];
    for (var index = 0; index < candidates.length; index += 1) {
      if (metadataIsComplete(candidates[index])) return candidates[index];
    }
    return primary || metadataFromDataset(null);
  }

  function browserController(windowObject) {
    if (!windowObject || !windowObject.document || windowObject.__enigmaSingleCartLimitReady) return;
    windowObject.__enigmaSingleCartLimitReady = true;

    var document = windowObject.document;
    var rootPath = windowObject.Shopify && windowObject.Shopify.routes && windowObject.Shopify.routes.root
      ? windowObject.Shopify.routes.root
      : '/';
    if (rootPath.slice(-1) !== '/') rootPath += '/';
    var cartUrl = rootPath + 'cart.js';
    var addQueue = Promise.resolve();
    var refreshTimer = null;
    var lastCart = null;
    var refreshPromise = null;

    function cartController() {
      return windowObject.EnigmaCartController || null;
    }

    function request(url, options) {
      var abort = typeof windowObject.AbortController === 'function' ? new windowObject.AbortController() : null;
      if (abort) options.signal = abort.signal;
      var timer;
      return Promise.race([
        Promise.resolve().then(function() { return windowObject.fetch(url, options); }),
        new Promise(function(resolve, reject) {
          timer = windowObject.setTimeout(function() {
            if (abort) abort.abort();
            reject(new Error('Cart request timed out'));
          }, 15000);
        })
      ]).finally(function() { windowObject.clearTimeout(timer); });
    }

    function limitMessage() {
      var canvas = document.querySelector('.block_cart_canvas');
      return canvas && canvas.getAttribute('data-enigma-single-limit-message') || 'This quantity cannot be added.';
    }

    function fetchCart() {
      var controller = cartController();
      if (controller && typeof controller.readCart === 'function') return controller.readCart();
      return request(cartUrl, {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' },
        cache: 'no-store'
      }).then(function(response) {
        if (!response.ok) throw new Error(response.statusText || 'Cart read failed');
        return response.json();
      }).then(function(cart) {
        if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart payload');
        return cart;
      });
    }

    function formIsLimited(form) {
      return Boolean(form && form.getAttribute('data-enigma-single-cart-policy') === 'true');
    }

    function formVariantId(form) {
      var input = form.querySelector('[data-variant-id-input][name="id"]')
        || form.querySelector('input.current_variant_id[name="id"]')
        || form.querySelector('input[name="id"]')
        || form.querySelector('select[name="id"]');
      return variantId(input && input.value);
    }

    function formQuantity(form) {
      var input = form.querySelector('[name="quantity"]');
      return positiveInteger(input && input.value) || 1;
    }

    function formMetadata(form, id) {
      var options = Array.prototype.slice.call(form.querySelectorAll('option[value]'));
      var option = options.find(function(candidate) { return variantId(candidate.value) === id; });
      var metadata = option ? metadataFromDataset(option.dataset) : metadataFromDataset(form.dataset);
      if (!metadata.inventoryManagement) metadata.inventoryManagement = form.getAttribute('data-enigma-inventory-management') || '';
      if (!metadata.inventoryPolicy) metadata.inventoryPolicy = form.getAttribute('data-enigma-inventory-policy') || '';
      if (metadata.inventoryQuantity === '') metadata.inventoryQuantity = form.getAttribute('data-enigma-inventory-quantity') || '';
      return metadata;
    }

    function formErrorRegion(form) {
      var region = form.querySelector('[data-product-error], [data-enigma-single-limit-status]');
      if (region) return region;
      region = document.createElement('p');
      region.className = 'product-form__error-message enigma-single-limit-status';
      region.setAttribute('data-enigma-single-limit-status', '');
      region.setAttribute('role', 'alert');
      region.setAttribute('aria-live', 'polite');
      form.appendChild(region);
      return region;
    }

    function announceForm(form, message) {
      var region = formErrorRegion(form);
      region.textContent = message || '';
      region.hidden = !message;
    }

    function addButton(form) {
      return form.querySelector('[data-add-to-cart][name="add"], .product-form__cart-submit[name="add"], [name="add"]');
    }

    function setFormMaximum(form, cart) {
      if (!formIsLimited(form)) return;
      var id = formVariantId(form);
      if (!id) return;
      var metadata = formMetadata(form, id);
      var cap = effectiveLimit(metadata);
      var existing = quantityForVariant(cart, id, false);
      var maximum = cap.valid ? Math.max(0, cap.limit - existing) : 0;
      var quantity = form.querySelector('[name="quantity"]');
      var button = addButton(form);

      if (quantity) {
        quantity.setAttribute('max', String(maximum));
        quantity.setAttribute('data-enigma-single-maximum', String(maximum));
        var currentValue = positiveInteger(quantity.value) || 1;
        if (maximum > 0 && currentValue > maximum) quantity.value = String(maximum);
      }

      if (!button) return;
      if (maximum < 1) {
        if (!button.disabled || form.getAttribute('data-cart-add-busy') === 'true') {
          button.setAttribute('data-enigma-policy-disabled', 'true');
        }
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      } else if (button.getAttribute('data-enigma-policy-disabled') === 'true') {
        button.removeAttribute('data-enigma-policy-disabled');
        var purchasable = form.getAttribute('data-product-purchasable');
        var state = form.getAttribute('data-product-state');
        if (purchasable !== 'false' && state !== 'sold-out' && state !== 'unavailable' && state !== 'error') {
          button.disabled = false;
          button.setAttribute('aria-disabled', 'false');
        }
      }
    }

    function rowsForCart() {
      return Array.prototype.slice.call(document.querySelectorAll([
        '[data-section-type="cart-template"] .cart_item',
        '#cart-info .ajaxcart__product',
        '.cart__popup .cart__popup-item'
      ].join(',')));
    }

    function rowItem(cart, row, index) {
      var key = row.getAttribute('data-cart-key');
      if (key) {
        var keyed = findCartItem(cart, key);
        if (keyed) return keyed;
      }
      return cartItems(cart)[index] || null;
    }

    function directRowMetadata(row) {
      var input = row.querySelector('.cart__qty-input, .cart__mini-qty--input, .cart__popup-qty--input');
      var source = input && input.hasAttribute('data-enigma-inventory-quantity') ? input : row;
      return metadataFromDataset(source && source.dataset);
    }

    function rowMetadata(row) {
      var primary = directRowMetadata(row);
      if (metadataIsComplete(primary)) return primary;
      var key = row && row.getAttribute('data-cart-key');
      var variant = row && row.getAttribute('data-cart-variant-id');
      var fallbacks = rowsForCart().filter(function(candidate) {
        if (candidate === row) return false;
        var candidateKey = candidate.getAttribute('data-cart-key');
        var candidateVariant = candidate.getAttribute('data-cart-variant-id');
        return Boolean(
          (key && candidateKey === key)
          || (variant && candidateVariant === variant)
        );
      }).map(directRowMetadata);
      return selectCompleteMetadata(primary, fallbacks);
    }

    function syncCartSurfaces(cart) {
      lastCart = cart;
      var limitedIds = [];
      var inventoryViolation = false;
      var surfaceIndexes = new Map();
      rowsForCart().forEach(function(row) {
        var surface = row.closest('[data-section-type="cart-template"]') || row.closest('#cart-info') || row.closest('.cart__popup') || document.body;
        var index = surfaceIndexes.get(surface) || 0;
        surfaceIndexes.set(surface, index + 1);
        var item = rowItem(cart, row, index);
        if (!item) return;
        var id = itemVariantId(item);
        var limited = itemIsLimited(item) || row.getAttribute('data-enigma-single-item') === 'true';
        if (!limited || !id) return;
        limitedIds.push(id);
        var input = row.querySelector('.cart__qty-input, .cart__mini-qty--input, .cart__popup-qty--input');
        if (!input) return;
        var metadata = rowMetadata(row);
        var maximum = lineMaximum(cart, {
          key: item.key,
          variantId: id,
          limited: true,
          metadata: metadata
        });
        var cap = effectiveLimit(metadata);
        if (!cap.valid || quantityForVariant(cart, id, false) > cap.limit) inventoryViolation = true;
        input.setAttribute('max', String(maximum));
        input.setAttribute('data-enigma-single-maximum', String(maximum));
        input.setAttribute('data-enigma-single-item', 'true');
        input.setAttribute('data-enigma-variant-id', id);
        if (metadataIsComplete(metadata)) {
          input.setAttribute('data-enigma-inventory-management', String(metadata.inventoryManagement));
          input.setAttribute('data-enigma-inventory-policy', String(metadata.inventoryPolicy));
          input.setAttribute('data-enigma-inventory-quantity', String(metadata.inventoryQuantity));
        }
      });

      Array.prototype.slice.call(document.querySelectorAll('form[data-enigma-single-cart-policy="true"]')).forEach(function(form) {
        setFormMaximum(form, cart);
      });
      setCheckoutState(cart, limitedIds, inventoryViolation);
      return { limitedVariantIds: limitedIds, violation: cartHasViolation(cart, limitedIds) || inventoryViolation };
    }

    function setCheckoutState(cart, limitedIds, inventoryViolation) {
      var invalid = cartHasViolation(cart, limitedIds) || inventoryViolation === true;
      document.documentElement.toggleAttribute('data-enigma-single-cart-invalid', invalid);
      Array.prototype.slice.call(document.querySelectorAll('[data-cart-normal-checkout], button.cart__checkout, a.checkout-button')).forEach(function(control) {
        if (invalid) {
          if (!control.disabled) control.setAttribute('data-enigma-policy-disabled', 'true');
          if ('disabled' in control) control.disabled = true;
          control.setAttribute('aria-disabled', 'true');
        } else if (control.getAttribute('data-enigma-policy-disabled') === 'true') {
          if (!control.hasAttribute('data-cart-empty-disabled') && !document.documentElement.hasAttribute('data-cart-mutating')) {
            control.removeAttribute('data-enigma-policy-disabled');
            if ('disabled' in control) control.disabled = false;
            control.removeAttribute('aria-disabled');
          }
        }
      });
      Array.prototype.slice.call(document.querySelectorAll('.additional-checkout-buttons')).forEach(function(wrapper) {
        wrapper.hidden = invalid;
        wrapper.toggleAttribute('inert', invalid || document.documentElement.hasAttribute('data-cart-mutating'));
        if (invalid) wrapper.setAttribute('aria-hidden', 'true');
        else wrapper.removeAttribute('aria-hidden');
      });
      if (invalid) announceCart(limitMessage());
    }

    function announceCart(message) {
      Array.prototype.slice.call(document.querySelectorAll('[data-cart-live-region]')).forEach(function(region) {
        region.textContent = message || '';
      });
    }

    function refresh() {
      var controller = cartController();
      if (controller) {
        if (typeof controller.isBusy === 'function' && controller.isBusy()) return Promise.resolve(null);
        var current = typeof controller.getCart === 'function' ? controller.getCart() : lastCart;
        if (current && Array.isArray(current.items)) syncCartSurfaces(current);
        return Promise.resolve(current || null);
      }
      var hasLimitedContext = document.querySelector('form[data-enigma-single-cart-policy="true"], [data-enigma-single-item="true"]')
        || document.documentElement.hasAttribute('data-enigma-single-cart-invalid')
        || cartItems(lastCart).some(itemIsLimited);
      if (!hasLimitedContext || document.documentElement.hasAttribute('data-cart-mutating')) return Promise.resolve(null);
      if (refreshPromise) return refreshPromise;
      refreshPromise = fetchCart().then(function(cart) {
        syncCartSurfaces(cart);
        return cart;
      }).finally(function() { refreshPromise = null; });
      return refreshPromise;
    }

    function scheduleRefresh() {
      windowObject.clearTimeout(refreshTimer);
      refreshTimer = windowObject.setTimeout(function() { refresh().catch(function() {}); }, 0);
    }

    function addAndVerify(form, button, id, expected) {
      if (!windowObject.jQuery) return Promise.reject(new Error(limitMessage()));
      return new Promise(function(resolve, reject) {
        var events = windowObject.jQuery(document);
        var eventName = 'ajaxComplete.enigmaSingleCartLimit';
        var timeout = windowObject.setTimeout(function() {
          events.off(eventName, completed);
          reject(new Error('Cart request timed out'));
        }, 15000);
        function completed(event, xhr, settings) {
          if (!settings || !/(?:^|\/)cart\/add(?:\.js)?\/?(?:\?|$)/.test(settings.url || '')) return;
          var parameters = new windowObject.URLSearchParams(settings.data || '');
          if (variantId(parameters.get('id')) !== id) return;
          events.off(eventName, completed);
          windowObject.clearTimeout(timeout);
          resolve();
        }
        events.on(eventName, completed);
        approveClick(form, button);
      }).then(function() {
        // A rejected add can still change the cart; reconcile once without polling or retrying the POST.
        return fetchCart();
      }).then(function(cart) {
        syncCartSurfaces(cart);
        if (quantityForVariant(cart, id, false) < expected) throw new Error('Cart addition was not confirmed');
        return cart;
      });
    }

    function releaseButton(button) {
      if (!button) return;
      button.removeAttribute('data-enigma-policy-busy');
      button.removeAttribute('aria-busy');
    }

    function approveClick(form, button) {
      form.setAttribute('data-enigma-policy-submit-approved', 'true');
      button.setAttribute('data-enigma-policy-click-approved', 'true');
      button.click();
      windowObject.setTimeout(function() {
        form.removeAttribute('data-enigma-policy-submit-approved');
        button.removeAttribute('data-enigma-policy-click-approved');
      }, 0);
    }

    function processAdd(form, button) {
      var controller = cartController();
      if (button.classList.contains('product-form__cart-submit') && controller && typeof controller.add === 'function') {
        return Promise.resolve().then(function() {
          return controller.add(form, button);
        }).catch(function() {
          return null;
        }).finally(function() {
          releaseButton(button);
        });
      }
      var id = formVariantId(form);
      var quantity = formQuantity(form);
      var metadata = formMetadata(form, id);
      announceForm(form, '');

      return fetchCart().then(function(cart) {
        var result = evaluateAddition(cart, {
          limited: true,
          variantId: id,
          quantity: quantity,
          metadata: metadata
        });
        if (!result.allowed) {
          setFormMaximum(form, cart);
          announceForm(form, limitMessage());
          return null;
        }

        if (button.classList.contains('product-form__cart-submit')) return addAndVerify(form, button, id, result.total);
        approveClick(form, button);
        return null;
      }).catch(function(error) {
        announceForm(form, error && error.message || limitMessage());
        return null;
      }).finally(function() {
        releaseButton(button);
      });
    }

    function queueAdd(form, button) {
      button.setAttribute('data-enigma-policy-busy', 'true');
      button.setAttribute('aria-busy', 'true');
      var add = function() { return processAdd(form, button); };
      addQueue = addQueue.then(add, add);
      return addQueue;
    }

    document.addEventListener('click', function(event) {
      var button = event.target.closest && event.target.closest('[data-add-to-cart][name="add"], .product-form__cart-submit[name="add"], [name="add"]');
      var form = button && button.form;
      if (!button || !formIsLimited(form)) return;
      if (button.getAttribute('data-enigma-policy-click-approved') === 'true') {
        button.removeAttribute('data-enigma-policy-click-approved');
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      if (button.getAttribute('data-enigma-policy-busy') === 'true') return;
      queueAdd(form, button);
    }, true);

    document.addEventListener('submit', function(event) {
      var form = event.target;
      if (!formIsLimited(form)) return;
      if (form.getAttribute('data-enigma-policy-submit-approved') === 'true') {
        form.removeAttribute('data-enigma-policy-submit-approved');
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      var button = event.submitter || addButton(form);
      if (button && button.getAttribute('data-enigma-policy-busy') !== 'true') queueAdd(form, button);
    }, true);

    document.addEventListener('change', function(event) {
      if (event.target.closest && event.target.closest('form[data-enigma-single-cart-policy="true"]')) scheduleRefresh();
    }, true);

    document.addEventListener('enigma:cart-updated', function(event) {
      var cart = event.detail && event.detail.cart;
      if (cart && Array.isArray(cart.items)) syncCartSurfaces(cart);
      else scheduleRefresh();
    });

    new windowObject.MutationObserver(function(mutations) {
      var relevant = mutations.some(function(mutation) {
        return Array.prototype.some.call(mutation.addedNodes, function(node) {
          return node.nodeType === 1 && (
            node.matches && node.matches('form[data-enigma-single-cart-policy="true"], #cart-info, .cart__popup, [data-section-type="cart-template"]')
            || node.querySelector && node.querySelector('form[data-enigma-single-cart-policy="true"], #cart-info, .cart__popup, [data-section-type="cart-template"]')
          );
        });
      });
      if (relevant) scheduleRefresh();
    }).observe(document.documentElement, { childList: true, subtree: true });

    new windowObject.MutationObserver(function() {
      if (!document.documentElement.hasAttribute('data-cart-mutating')) scheduleRefresh();
    }).observe(document.documentElement, { attributes: true, attributeFilter: ['data-cart-mutating'] });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scheduleRefresh, { once: true });
    } else {
      scheduleRefresh();
    }

    policy.fetchCart = fetchCart;
    policy.refresh = refresh;
    policy.syncCartSurfaces = syncCartSurfaces;
    policy.announceCart = announceCart;
  }

  var policy = {
    MAX_COPIES: MAX_COPIES,
    LIMITED_PRODUCT_TYPE: LIMITED_PRODUCT_TYPE,
    normalize: normalize,
    variantId: variantId,
    isLimitedProductType: isLimitedProductType,
    itemIsLimited: itemIsLimited,
    quantityForVariant: quantityForVariant,
    aggregateLimitedQuantities: aggregateLimitedQuantities,
    effectiveLimit: effectiveLimit,
    evaluateAddition: evaluateAddition,
    evaluateLineChange: evaluateLineChange,
    lineMaximum: lineMaximum,
    cartHasViolation: cartHasViolation,
    metadataFromDataset: metadataFromDataset,
    metadataFieldsDeclared: metadataFieldsDeclared,
    selectCompleteMetadata: selectCompleteMetadata,
    browserController: browserController
  };

  if (typeof window !== 'undefined') browserController(window);
  return policy;
});
